import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Styled Components
const Container = styled.div`
  font-family: Arial, sans-serif;
  padding: 40px;
  max-width: 1200px;
  margin: auto;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StyledSlider = styled(Slider)`
  .slick-slide {
    padding: 10px;
  }
`;

const Slide = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: center;
  color: white;
  padding: 20px;
  font-size: 18px;
  font-weight: bold;
  transition: transform 0.3s, box-shadow 0.3s;
  background-image: ${({ bgImage }) => `url(${bgImage})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${({ isActive }) =>
    isActive &&
    `box-shadow: 5px 5px 5px 5px rgba(81, 160, 239, 0.9);`}

  &:hover {
    transform: scale(1.05);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 140, 0.9), transparent);
    z-index: 1;
  }
`;

const SlideContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  z-index: 2;
`;

const DynamicButton = styled.a`
  display: inline-block;
  text-decoration: none;
  background: ${({ color }) => color || "transparent"};
  border: 2px solid ${({ color }) => color || "blue"};
  padding: 10px 25px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 10px;
  color: white;
  border-radius: 10px;
  transition: background 0.3s, color 0.3s;

  &:hover {
    background: ${({ hoverColor }) => hoverColor || "rgb(16, 60, 235)"};
    color: white;
  }
`;

const ArticleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  margin-top: 40px;
`;

const ArticleContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 300px;
`;

const ArticleTitle = styled.h3`
  font-size: 22px;
  font-weight: bold;
  width: 300px;
`;

const ReadMoreButton = styled.a`
  text-decoration: none;
  background: ${({ color }) => color || "rgb(19, 97, 193)"};
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  margin-top: 10px;
  color: white;
  display: inline-block;
  border-radius: 5px;
  transition: background 0.3s;
  
  &:hover {
    background: ${({ hoverColor }) => hoverColor || "rgb(10, 70, 170)"};
  }
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  font-size: 14px;
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: gray;
`;

// Main Component
const ArticlePage = () => {
  const [articles, setArticles] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    fetch("https://pristineacademicconsultancy.com/Admin/get_article.php")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setArticles(data);
        } else {
          console.error("Invalid API response:", data);
        }
      })
      .catch((error) => console.error("Error fetching articles:", error));
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
    centerMode: true,
    centerPadding: "0px",
    beforeChange: (_, next) => setActiveSlide(next),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <Container>
      <StyledSlider {...settings}>
        {articles.map((article, index) => (
          <Slide key={index} bgImage={article.image_url} isActive={index === activeSlide}>
            <SlideContent>
              <div>{article.title}</div>
              <DynamicButton
                href={article.button_link || "#"}
                color={article.buttonColor}
                hoverColor={article.hoverButtonColor}
              >
                {article.buttonText || "Discover"}
              </DynamicButton>
            </SlideContent>
          </Slide>
        ))}
      </StyledSlider>

      {/* Article Section */}
      {articles.length > 0 && (
        <ArticleSection>
          <ArticleContent>
            <Title>Articles</Title>
            <ArticleTitle>{articles[activeSlide]?.title || "No Title"}</ArticleTitle>
            <ReadMoreButton
              href={articles[activeSlide]?.button_link || "#"}
              color={articles[activeSlide]?.buttonColor}
              hoverColor={articles[activeSlide]?.hoverButtonColor}
            >
              {articles[activeSlide]?.buttonText || "READ MORE"}
            </ReadMoreButton>
          </ArticleContent>

          <p>{articles[activeSlide]?.content || "No content available."}</p>
        </ArticleSection>
      )}

      {/* Profile Section */}
      {articles.length > 0 && (
        <Profile>
          <ProfileImage
            src={articles[activeSlide]?.writer_image || "/logos/logo.jpg"}
            alt="Writer"
          />
          <div>
            {articles[activeSlide]?.writer || "Unknown"} -{" "}
            {articles[activeSlide]?.date || "No Date"}
          </div>
        </Profile>
      )}
    </Container>
  );
};

export default ArticlePage;
